import { useState, useCallback } from 'react';
import { Deal } from '../../lib/types';
import { useApp } from '../../components/AppProvider';
import { useErrorHandler } from '../useErrorHandler';

interface UseDealActionsHandlerResult {
  showEditForm: boolean;
  setShowEditForm: React.Dispatch<React.SetStateAction<boolean>>;
  showTemplateModal: boolean;
  setShowTemplateModal: React.Dispatch<React.SetStateAction<boolean>>;
  showFileUploadModal: boolean;
  setShowFileUploadModal: React.Dispatch<React.SetStateAction<boolean>>;
  showSignatureLinkModal: boolean;
  setShowSignatureLinkModal: React.Dispatch<React.SetStateAction<boolean>>;
  showStatusUpdateDrawer: boolean;
  setShowStatusUpdateDrawer: React.Dispatch<React.SetStateAction<boolean>>;
  showContractViewerModal: boolean;
  setShowContractViewerModal: React.Dispatch<React.SetStateAction<boolean>>;
  showSendProposalModal: boolean;
  setShowSendProposalModal: React.Dispatch<React.SetStateAction<boolean>>;
  handleUpdateDeal: (formData: any, services: any[], commissions?: any, referrerIds?: string[], contactIds?: string[]) => Promise<void>;
}

export const useDealActionsHandler = (
  currentDeal: Deal | null,
  updateDeal: (id: string, deal: Partial<Deal>, services?: any[], contactIds?: string[]) => Promise<Deal | null>,
  fetchDealServices: (id: string) => Promise<void>,
  onSuccess?: () => void
): UseDealActionsHandlerResult => {
  const { toast } = useApp();
  const { withErrorHandling } = useErrorHandler();
  // UI states
  const [showEditForm, setShowEditForm] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState<boolean>(false);
  const [showFileUploadModal, setShowFileUploadModal] = useState(false);
  const [showSignatureLinkModal, setShowSignatureLinkModal] = useState(false);
  const [showStatusUpdateDrawer, setShowStatusUpdateDrawer] = useState(false);
  const [showContractViewerModal, setShowContractViewerModal] = useState(false);
  const [showSendProposalModal, setShowSendProposalModal] = useState(false);

  // `setShowTemplateModal` era un wrapper che, all'apertura, preselezionava il
  // primo template. Non ha mai funzionato: leggeva `selectedTemplateId` e
  // `setSelectedTemplateId` da `useQuoteTemplateStore`, che non li espone — la
  // guardia `typeof ... === 'function'` rendeva il ramo silenziosamente morto.
  // La preselezione la fa già TemplateModal con un effetto suo, e il vero
  // `selectedTemplateId` vive in `useSignatureSharing`. Qui resta il setter.

  // Update deal handler
  const handleUpdateDeal = useCallback(async (formData: any, services?: any[], commissions?: any, referrerIds?: string[], contactIds?: string[]) => {
    if (!currentDeal) return;

    console.log('🔄 [useDealActionsHandler] handleUpdateDeal called with:', { formData, services: services?.length, commissions, referrerIds, contactIds });

    await withErrorHandling(async () => {
      const updatedDeal = await updateDeal(currentDeal.id, formData, services, contactIds);

      // updateDeal ritorna null quando il salvataggio fallisce (l'errore viene
      // inghiottito nel suo catch): senza questo check si mostrava il toast di
      // successo e la pagina restava con i dati vecchi senza alcun segnale.
      if (!updatedDeal) {
        throw new Error("Salvataggio dell'offerta non riuscito. Riprova.");
      }

      // Save commission overrides - always call to handle both setting and clearing values
      // When user_commission_rate is undefined, createDealCommissionOverride will delete the override
      if (commissions && currentDeal.id) {
        const { useDealCommissionStore } = await import('../../lib/stores/dealCommissionStore');
        const { createDealCommissionOverride } = useDealCommissionStore.getState();

        console.log('💰 [useDealActionsHandler] Saving commission overrides for deal:', currentDeal.id, commissions);
        await createDealCommissionOverride(currentDeal.id, commissions);
        // P12: il ricalcolo provvigioni è event-driven (trigger DB su deal_user_commission_overrides
        // e deal_referrers → core), nessuna invalidazione client-side necessaria
      }

      // Save referrers if provided
      if (referrerIds !== undefined && currentDeal.id) {
        const { useReferrerStore } = await import('../../lib/stores/referrerStore');
        const { fetchDealReferrers, addDealReferrer, removeDealReferrer, triggerDealReferrersRefresh } = useReferrerStore.getState();

        console.log('👥 [useDealActionsHandler] Syncing referrers for deal:', currentDeal.id, referrerIds);

        // Get current referrers
        const currentReferrers = await fetchDealReferrers(currentDeal.id);
        const currentReferrerIds = currentReferrers.map(r => r.referrer_id);

        // Find referrers to remove (in current but not in new list)
        const toRemove = currentReferrers.filter(r => !referrerIds.includes(r.referrer_id));
        for (const referrer of toRemove) {
          console.log('➖ Removing referrer:', referrer.referrer_id);
          await removeDealReferrer(referrer.id);
        }

        // Find referrers to add (in new list but not in current)
        const toAdd = referrerIds.filter(id => !currentReferrerIds.includes(id));
        for (const referrerId of toAdd) {
          console.log('➕ Adding referrer:', referrerId);
          await addDealReferrer(currentDeal.id, referrerId);
        }

        // Trigger refresh of DealReferrerSection if referrers changed
        if (toAdd.length > 0 || toRemove.length > 0) {
          console.log('🔄 [useDealActionsHandler] Triggering referrers refresh');
          triggerDealReferrersRefresh();

          // Trigger commission recalculation for accepted deals with new referrers
          if (toAdd.length > 0 && currentDeal.status === 'accepted' && currentDeal.organization_id) {
            console.log('💰 [useDealActionsHandler] Triggering commission recalculation for accepted deal');
            try {
              const { commissionCacheService } = await import('../../lib/services/commissionCacheService');
              const closedDate = currentDeal.closed_at ? new Date(currentDeal.closed_at) : new Date();
              await commissionCacheService.triggerCalculation(
                currentDeal.organization_id,
                closedDate,
                undefined, // triggeredBy
                true // forceRecalculation
              );
              console.log('✅ [useDealActionsHandler] Commission recalculation triggered successfully');
            } catch (commissionError) {
              console.error('❌ [useDealActionsHandler] Error triggering commission recalculation:', commissionError);
            }
          }
        }
      }

      toast.success('Offerta aggiornata correttamente');
      setShowEditForm(false);

      // Refresh services
      if (currentDeal.id) {
        await fetchDealServices(currentDeal.id);
      }

      if (onSuccess) {
        onSuccess();
      }

      return updatedDeal;
    }, {
      defaultMessage: 'Failed to update deal'
    });
  }, [currentDeal, updateDeal, fetchDealServices, toast, withErrorHandling, onSuccess]);

  return {
    showEditForm,
    setShowEditForm,
    showTemplateModal,
    setShowTemplateModal,
    showFileUploadModal,
    setShowFileUploadModal,
    showSignatureLinkModal,
    setShowSignatureLinkModal,
    showStatusUpdateDrawer,
    setShowStatusUpdateDrawer,
    showContractViewerModal,
    setShowContractViewerModal,
    showSendProposalModal,
    setShowSendProposalModal,
    handleUpdateDeal
  };
};